import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { formatCommissionAmount } from '../utils/formatters';
import './SalesManagement.css';

interface OperatorCommission {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  maxAllowedPercentage: number; // What upline set as max
  ownTakePercentage: number;    // What I'm currently taking
  baseAllocationPercentage: number; // Always 60%
}

interface EditData {
  operatorId: number;
  operatorName: string;
  maxAllowed: number;
  currentTake: number;
  newTake: number;
}

const SalesManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<EditData | null>(null);
  const [newTakeValue, setNewTakeValue] = useState<string | number>(0);

  // Fetch operator commissions data
  const { data: operatorData, isLoading, error } = useQuery<OperatorCommission[]>({
    queryKey: ['my-operator-commissions'],
    queryFn: async () => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/HierarchicalCommission/my-operator-commissions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return []; // No data found, return empty array
        }
        throw new Error('Failed to fetch operator commissions');
      }
      
      return response.json();
    }
  });

  // Update operator commission mutation
  const updateMutation = useMutation({
    mutationFn: async (request: { operatorCommissions: Array<{ operatorId: number; ownTakePercentage: number }> }) => {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/HierarchicalCommission/my-operator-commissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update operator commission');
      }

      return response.json();
    },
    onSuccess: () => {
      enqueueSnackbar('Commission updated successfully!', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['my-operator-commissions'] });
      setEditDialogOpen(false);
      setEditData(null);
      setNewTakeValue(0);
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleEditClick = (operator: OperatorCommission) => {
    setEditData({
      operatorId: operator.operatorId,
      operatorName: operator.operatorName,
      maxAllowed: operator.maxAllowedPercentage, // Already in RM (e.g., 0.60)
      currentTake: operator.ownTakePercentage,   // Already in RM (e.g., 0.50)
      newTake: operator.ownTakePercentage
    });
    setNewTakeValue(operator.ownTakePercentage); // Use raw amount
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    if (!editData) return;

    const amount = typeof newTakeValue === 'string' ? parseFloat(newTakeValue) : newTakeValue;
    const request = {
      operatorCommissions: [{
        operatorId: editData.operatorId,
        ownTakePercentage: amount || 0
      }]
    };

    updateMutation.mutate(request);
  };

  const handleClose = () => {
    setEditDialogOpen(false);
    setEditData(null);
    setNewTakeValue(0);
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">
          Error loading commission data: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!operatorData || operatorData.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box className="page-header">
          <Typography variant="h4" component="h1" className="page-title">
            <TrendingUpIcon className="title-icon" />
            Sales Management
          </Typography>
          <Typography variant="body1" className="page-description">
            Set your actual sales percentages for each operator within the limits set by your upline.
          </Typography>
        </Box>
        
        <Alert severity="info" className="info-alert">
          <InfoIcon />
          No operator commission data found. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <TrendingUpIcon className="title-icon" />
          Sales Management
        </Typography>
        <Typography variant="body1" className="page-description">
          Set your commission amount (RM/set) for each operator within the limits set by your upline.
        </Typography>
      </Box>

      <Alert severity="info" className="info-alert">
        <InfoIcon />
        Configure how much amount (RM) you want to take per set from each operator. You cannot exceed the maximum allowed amount set by your upline.
      </Alert>

      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: 3,
          mt: 2
        }}
      >
        {operatorData.map((operator) => {
          const maxAllowed = operator.maxAllowedPercentage; // Already in RM (e.g., 0.60)
          const currentTake = operator.ownTakePercentage;   // Already in RM (e.g., 0.50)
          const remaining = maxAllowed - currentTake;

          return (
            <Box key={operator.operatorId}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Operator Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {operator.operatorName}
                    </Typography>
                    {/* Hide edit button for Admin users */}
                    {user?.role !== 'admin' && (
                      <IconButton 
                        onClick={() => handleEditClick(operator)}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>

                  {/* Commission Details */}
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Max Allowed/set:
                      </Typography>
                      <Chip 
                        label={formatCommissionAmount(maxAllowed)}
                        size="small"
                        color="info"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary">
                        Your Take/set:
                      </Typography>
                      {user?.role === 'admin' ? (
                        <Chip 
                          label="REMAINDER"
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : (
                        <Chip 
                          label={formatCommissionAmount(currentTake)}
                          size="small"
                          color="success"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Remaining/set:
                      </Typography>
                      <Chip 
                        label={formatCommissionAmount(remaining)}
                        size="small"
                        color={remaining > 0 ? "default" : "error"}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Usage: {formatCommissionAmount(currentTake)} of {formatCommissionAmount(maxAllowed)}
                    </Typography>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 8, 
                        backgroundColor: 'grey.200', 
                        borderRadius: 1,
                        overflow: 'hidden'
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: `${(currentTake / maxAllowed) * 100}%`, 
                          height: '100%', 
                          backgroundColor: currentTake > maxAllowed * 0.8 ? 'error.main' : 'success.main',
                          transition: 'width 0.3s ease'
                        }} 
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Commission Amount (RM/set)
        </DialogTitle>
        <DialogContent>
          {editData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Operator: <strong>{editData.operatorName}</strong>
              </Typography>
              
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Settings:
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Max Allowed/set:</Typography>
                  <Chip label={formatCommissionAmount(editData.maxAllowed)} size="small" color="info" />
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Current Take/set:</Typography>
                  <Chip label={formatCommissionAmount(editData.currentTake)} size="small" color="success" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Available/set:</Typography>
                  <Chip label={formatCommissionAmount(editData.maxAllowed - editData.currentTake)} size="small" color="default" />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Your Take Amount (RM/set)"
                type="text"
                value={newTakeValue}
                onChange={(e) => {
                  // Allow free typing including partial values like "0.", "0.5"
                  setNewTakeValue(e.target.value);
                }}
                onBlur={(e) => {
                  // Validate and clamp to max allowed from DB (set by upline)
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    const clamped = Math.min(Math.max(value, 0), editData.maxAllowed);
                    setNewTakeValue(clamped);
                  } else {
                    setNewTakeValue(0);
                  }
                }}
                helperText={`Maximum allowed: ${formatCommissionAmount(editData.maxAllowed)}${typeof newTakeValue === 'number' && newTakeValue > editData.maxAllowed ? ' (will reduce upline allocation)' : ''}`}
                sx={{ mt: 3 }}
              />

              {typeof newTakeValue === 'number' && newTakeValue > editData.maxAllowed && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                  ⚠️ This will automatically reduce your upline's allocation to accommodate your request
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={updateMutation.isPending}
            startIcon={<SaveIcon />}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SalesManagement;